import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { COMPARISONS } from "@/data/comparisons";
import { brandedTitle, safeDesc } from "@/lib/seo-meta";

const SITE = "https://heallexa.com";

const META_TITLE = brandedTitle("Heallexa vs Zocdoc & Top Healthcare Directories");
const META_DESC = safeDesc(
  "Compare Heallexa with Zocdoc, Healthgrades, WebMD, Vitals, Sesame, and more. Find the best healthcare directory for patients and providers in the U.S."
);

export const Route = createFileRoute("/compare/")({
 head: () => ({
 meta: [
 { title: META_TITLE },
 { name: "description", content: META_DESC },
 { property: "og:title", content: META_TITLE },
 { property: "og:description", content: META_DESC },
 { property: "og:url", content: `${SITE}/compare` },
 { property: "og:type", content: "website" },
 { name: "twitter:title", content: META_TITLE },
 { name: "twitter:description", content: META_DESC },
 ],
 links: [{ rel: "canonical", href: `${SITE}/compare` }],
 scripts: [
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "FAQPage",
 mainEntity: [
 {
 "@type": "Question",
 name: "What is Heallexa?",
 acceptedAnswer: {
 "@type": "Answer",
 text: "Heallexa is an AI-powered healthcare directory that helps patients find doctors, dentists, therapists, and other providers by specialty, insurance, location, and availability. It is free for patients and offers free and premium listing plans for providers.",
 },
 },
 {
 "@type": "Question",
 name: "How does Heallexa compare to Zocdoc?",
 acceptedAnswer: {
 "@type": "Answer",
 text: "Heallexa offers AI-powered provider discovery, claimable listings, and appointment requests. Unlike Zocdoc, Heallexa has a free listing tier and covers all healthcare specialties, not just those inside a booking network.",
 },
 },
 {
 "@type": "Question",
 name: "Is Heallexa free for patients?",
 acceptedAnswer: {
 "@type": "Answer",
 text: "Yes. Searching, browsing, and viewing provider details are completely free for patients on Heallexa. No account is required to search.",
 },
 },
 {
 "@type": "Question",
 name: "Is Heallexa free for providers?",
 acceptedAnswer: {
 "@type": "Answer",
 text: "Heallexa offers a free listing tier with a public profile, phone, address, and website. Premium plans with additional visibility and features start at $29 per month.",
 },
 },
 ],
 }),
 },
 {
 type: "application/ld+json",
 children: JSON.stringify({
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "Home", item: SITE },
 { "@type": "ListItem", position: 2, name: "Compare", item: `${SITE}/compare` },
 ],
 }),
 },
 ],
 }),
 component: CompareHubPage,
});

function CompareHubPage() {
 return (
 <div className="min-h-screen bg-background pb-24 md:pb-1">
 <Navbar />

 <header className="pt-32 md:pt-24 pb-16 border-b border-border bg-card">
 <div className="mx-auto max-w-[1200px] px-6 text-center">
 <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">Compare healthcare directories</p>
 <h1 className="font-display text-[clamp(2.25rem,5vw,3.75rem)] font-medium leading-[1.05] tracking-[-0.04em] text-foreground max-w-4xl mx-auto">
 Compare Heallexa With Other Healthcare Directories
 </h1>
 <p className="mt-6 text-base text-muted-foreground leading-relaxed max-w-[560px] mx-auto">
 See how Heallexa compares with popular doctor booking platforms, healthcare directories, review sites, and provider listing platforms. Heallexa helps patients find care faster while giving providers a simple way to claim, manage, and grow their online presence.
 </p>
 <div className="mt-8 flex flex-wrap justify-center gap-3">
 <Button asChild size="lg"><Link to="/search">Find a Provider</Link></Button>
 <Button asChild size="lg" variant="outline"><Link to="/list-your-practice">List Your Practice</Link></Button>
 <Button asChild size="lg" variant="outline"><Link to="/browse">Claim Your Listing</Link></Button>
 </div>
 </div>
 </header>

 <main className="mx-auto max-w-[1200px] px-6 py-16">
 <div className="flex items-end justify-between gap-6 mb-8">
 <div>
 <h2 className="font-display text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground">Side-by-side comparisons</h2>
 <p className="mt-3 text-[14px] text-muted-foreground max-w-[520px]">Pick a platform to see how it compares to Heallexa.</p>
 </div>
 </div>

 <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
 {COMPARISONS.map((c, idx) => (
 <Reveal key={c.slug} as="li" from="up" delay={Math.min(idx, 8) * 70} className="h-full">
 <Link
 to="/compare/$slug"
 params={{ slug: c.slug }}
 className="group hover-lift cursor-pointer card-surface p-6 sm:p-7 flex flex-col rounded-md h-full"
 >
 <p className="text-[11px] font-semibold text-primary tracking-[0.18em] uppercase">Comparison</p>
 <h3 className="font-display mt-2 text-[16px] font-semibold text-foreground leading-tight">Heallexa vs {c.competitor}</h3>
 <p className="mt-3 flex-1 text-[13.5px] text-muted-foreground line-clamp-3">
 {c.primaryKeyword}, see how Heallexa stacks up for patients and providers.
 </p>
 <span className="mt-4 pt-4 border-t border-border/60 flex items-center justify-end text-[12.5px]">
 <span className="inline-flex items-center gap-1 font-medium text-primary group-hover:gap-2 transition-all">
 Read comparison <ArrowRight className="h-3.5 w-3.5" />
 </span>
 </span>
 </Link>
 </Reveal>
 ))}
 </ul>

 <Reveal from="up" delay={60}>
 <section className="mt-16 card-surface rounded-md p-8 sm:p-10 text-center">
 <h2 className="font-display text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground">Ready to Find Care or Grow Your Practice?</h2>
 <p className="mt-3 text-[14px] text-muted-foreground max-w-2xl mx-auto">
 Whether you are a patient looking for the right provider or a healthcare practice looking for more visibility, Heallexa gives you a simple way to connect.
 </p>
 <div className="mt-6 flex flex-wrap justify-center gap-3">
 <Button asChild size="lg"><Link to="/search">Find a Provider</Link></Button>
 <Button asChild size="lg" variant="outline"><Link to="/browse">Claim Your Listing</Link></Button>
 <Button asChild size="lg" variant="outline"><Link to="/list-your-practice">List Your Practice</Link></Button>
 </div>
 </section>
 </Reveal>

 <p className="mt-10 text-xs text-muted-foreground text-center">
 Comparison information is for general educational purposes and may change. Please visit each platform directly for the most current details.
 </p>
 </main>

 <FooterSection />

 <div className="fixed bottom-0 inset-x-0 z-40 md:hidden border-t border-border bg-card/95 backdrop-blur p-3 flex gap-2">
 <Button asChild className="flex-1"><Link to="/search">Find Provider</Link></Button>
 <Button asChild variant="outline" className="flex-1"><Link to="/list-your-practice">List Practice</Link></Button>
 </div>
 </div>
 );
}
